import { Grid2 as Grid, Typography } from '@mui/material';

import {
  CloseFormShowButton,
  EditFormShowButton,
  InputList,
  SaveFormShowButton,
  useFormShowContext,
} from 'framework/form';
import {
  EditableListDataGridLayout,
  EditableListFormShowTemplate,
  EDITABLE_LIST_FIELD,
  EDITABLE_LIST_GROUP,
  EDITABLE_LIST_NESTED_LIST,
} from 'framework/list';
import { createTruncatedTextFormat, FormattedTextField } from 'framework/ui/field';
import {
  FTEBadge,
  MapedExpectedStructure,
  ProjectTeamIssuesField,
  ProjectTeamMemberPositionField,
  ProjectTeamMemberRoleField,
  ProjectTeamSlotPositionField,
  TeamMemberField,
} from 'domain/shared/newProject';
import { AssignmentDateRangeField } from 'domain/shared/assignment';
import TeamPositionsInputs from 'domain/shared/newProject/team/input/TeamPositionsInputs';

const ProjectTeamPositionsList = () => {
  const { isEditMode } = useFormShowContext();

  return (
    <>
      <Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 4 }}>
        <Grid size="grow">
          <Typography variant="h4" color="primary">
            Team slots update
          </Typography>
        </Grid>
        <Grid size="auto" display="flex" alignItems="center">
          {isEditMode ? (
            <>
              <SaveFormShowButton key="save" />
              <CloseFormShowButton key="close" />
            </>
          ) : (
            <EditFormShowButton key="edit" />
          )}
        </Grid>
      </Grid>
      {isEditMode ? (
        <InputList
          inputTemplate={TeamPositionsInputs}
          source="team.expectedStructure"
          addText="Add position"
          disabledRemove={(slotData: MapedExpectedStructure) => !!slotData?.members?.length}
          removeButtonTooltipe={(slotData: MapedExpectedStructure) =>
            !!slotData?.members?.length ? 'Position has assignments.' : null
          }
          defaultValue={{}}
        />
      ) : (
        <EditableListFormShowTemplate listSource="team.expectedStructure">
          <EditableListDataGridLayout
            customEnptyMessage="No team positions available"
            getRowEnabled={() => true}
            newRowDefaultData={{
              members: [],
              issues: [],
              assignedFte: 0,
            }}
            rowProps={{
              px: 0,
              py: 1,
            }}
            getTableCells={() =>
              Promise.resolve([
                {
                  type: EDITABLE_LIST_GROUP,
                  component: Grid,
                  componentProps: {
                    container: true,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    columnSpacing: 1,
                    columns: 12,
                    borderBottom: '1px solid var(--mui-palette-TableCell-border)',
                    pb: 1,
                  },
                  gridCellProps: {
                    size: 12,
                  },
                  children: [
                    {
                      type: EDITABLE_LIST_FIELD,
                      source: 'positionId',
                      isEditor: false,
                      component: ProjectTeamSlotPositionField,
                      gridCellProps: { size: 2.5 },
                    },
                    {
                      type: EDITABLE_LIST_FIELD,
                      source: 'requiredFte',
                      isEditor: false,
                      component: FTEBadge,
                      componentProps: {
                        assignedFteSource: 'assignedFte',
                      },
                      gridCellProps: { size: 6 },
                    },
                    {
                      type: EDITABLE_LIST_FIELD,
                      source: 'description',
                      isEditor: false,
                      component: FormattedTextField,
                      componentProps: {
                        format: createTruncatedTextFormat(40),
                      },
                      gridCellProps: { size: 2 },
                    },
                    {
                      type: EDITABLE_LIST_FIELD,
                      source: 'issues',
                      isEditor: false,
                      component: ProjectTeamIssuesField,
                      gridCellProps: { size: 1.5 },
                    },
                  ],
                },
                {
                  type: EDITABLE_LIST_GROUP,
                  component: Grid,
                  componentProps: {
                    container: true,
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    columnSpacing: 1,
                    columns: 12,
                    pb: 1,
                    borderBottom: '1px solid var(--mui-palette-TableCell-border)',
                  },
                  gridCellProps: {
                    size: 12,
                  },
                  children: [
                    {
                      type: EDITABLE_LIST_NESTED_LIST,
                      source: 'members',
                      component: Grid,
                      gridCellProps: { size: 12 },
                      rows: {
                        emptyListComponent: (
                          <Typography variant="body2" py={1} color="textSecondary" pl={5}>
                            No assignments in this position.
                          </Typography>
                        ),
                      },
                      componentProps: {
                        container: true,
                        alignItems: 'center',
                        direction: 'row',
                        justifyContent: 'space-between',
                        pr: 0,
                        py: 1,
                        columnSpacing: 1,
                        columns: 12,
                      },
                      children: [
                        {
                          type: EDITABLE_LIST_FIELD,
                          source: 'employeeAlias',
                          isEditor: false,
                          component: TeamMemberField,
                          gridCellProps: { size: 2.5, sx: { pl: 5 } },
                        },
                        {
                          type: EDITABLE_LIST_FIELD,
                          source: 'fte',
                          isEditor: false,
                          component: FTEBadge,
                          componentProps: { isMemberFter: true },
                          gridCellProps: {
                            size: 0.7,
                            sx: { display: 'flex', alignItems: 'center' },
                          },
                        },
                        {
                          type: EDITABLE_LIST_FIELD,
                          source: 'positionId',
                          isEditor: false,
                          component: ProjectTeamMemberPositionField,
                          componentProps: {
                            positionIdSource: 'positionId',
                            technologyIdSource: 'technologyId',
                            competencyLevelSource: 'competencyLevel',
                          },
                          gridCellProps: { size: 2 },
                        },
                        {
                          type: EDITABLE_LIST_FIELD,
                          source: 'role',
                          isEditor: false,
                          component: ProjectTeamMemberRoleField,
                          gridCellProps: { size: 1.5 },
                        },
                        {
                          type: EDITABLE_LIST_FIELD,
                          source: 'startDate',
                          isEditor: false,
                          component: AssignmentDateRangeField,
                          gridCellProps: { size: 1.8 },
                        },
                        {
                          type: EDITABLE_LIST_FIELD,
                          source: 'noteText',
                          isEditor: false,
                          component: FormattedTextField,
                          componentProps: {
                            format: createTruncatedTextFormat(40),
                          },
                          gridCellProps: { size: 2 },
                        },
                        {
                          type: EDITABLE_LIST_FIELD,
                          source: 'issues',
                          isEditor: false,
                          component: ProjectTeamIssuesField,
                          gridCellProps: { size: 1.5 },
                        },
                      ],
                    },
                  ],
                },
              ])
            }
          />
        </EditableListFormShowTemplate>
      )}
    </>
  );
};

export default ProjectTeamPositionsList;
